import { AmbientGlow, GlassCard, ScrollReveal } from '@/components/design-system';
import ListingTimelineCard from '@/components/household/ListingTimelineCard';
import PageShell from '@/components/layout/PageShell';
import { EmptyState } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyListings, type ListingsResponse } from '@/lib/collections/listings';
import { useAuth } from '@pooflabs/web';
import { CheckCircle2, Clock, ListChecks, PackageSearch, Sparkles, Wallet } from 'lucide-react';

const SECTIONS: { status: 'open' | 'accepted' | 'scheduled' | 'completed'; label: string; icon: typeof Clock }[] = [
  { status: 'open', label: 'Open', icon: ListChecks },
  { status: 'accepted', label: 'Accepted', icon: Clock },
  { status: 'scheduled', label: 'Scheduled', icon: Sparkles },
  { status: 'completed', label: 'Completed', icon: CheckCircle2 },
];

/**
 * Household-facing tracker for the current wallet's own recovery listings,
 * realtime, grouped by pipeline stage with a per-card progress stepper.
 */
export function MyListingsPage() {
  const { user, login } = useAuth();

  const { data: listings, loading } = useRealtimeData<ListingsResponse[]>(
    subscribeManyListings,
    !!user?.address,
    `created by ${user?.address ?? ''}, newest first`
  );

  if (!user?.address) {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="amber" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Wallet className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">My Listings</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Connect to get started</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Connect your wallet to track your recovery listings.
              </p>
              <Button
                onClick={() => login()}
                size="lg"
                className="mt-6 w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
              >
                Connect wallet
              </Button>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  const mine = (listings ?? []).filter((l) => l.createdBy === user.address);

  return (
    <PageShell>
      <div className="container relative py-8">
        <AmbientGlow color="amber" size={420} position="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
        <AmbientGlow color="primary" size={380} position="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />

        <ScrollReveal className="mb-8">
          <span className="text-overline">My Listings</span>
          <h1 className="font-heading mt-1 text-3xl font-bold text-foreground sm:text-4xl">
            Track every recovery, step by step
          </h1>
        </ScrollReveal>

        {!loading && mine.length === 0 && (
          <EmptyState
            icon={<PackageSearch />}
            title="No listings yet"
            description="Scan and publish your first recovery listing from the Scan page."
          />
        )}

        <div className="relative space-y-10">
          {SECTIONS.map(({ status, label, icon: Icon }, sectionIndex) => {
            const group = mine.filter((l) => l.status === status);
            if (group.length === 0) return null;
            return (
              <div key={status}>
                <div className="mb-3 flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-sm font-semibold text-foreground">{label}</h2>
                  <span className="text-xs text-muted-foreground">({group.length})</span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {group.map((listing, i) => (
                    <ScrollReveal key={listing.id} delay={Math.min((sectionIndex * 3 + i) * 0.04, 0.3)}>
                      <ListingTimelineCard listing={listing} />
                    </ScrollReveal>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}

export default MyListingsPage;
